import * as THREE from "three";

export type BotTargetKind = "cube" | "jar" | "cone";

export interface BotTarget
{
  kind: BotTargetKind;
  position: THREE.Vector3;
  size: THREE.Vector3;
  table: number;
}

export interface BotGameApi
{
  isFinished(): boolean;
  getShotsLeft(): number;
  hasActiveBalls(): boolean;
  areTargetsSettled(): boolean;
  getAliveTargets(): BotTarget[];
  isReachable( world_point: THREE.Vector3 ): boolean;
  botFireAt( world_point: THREE.Vector3 ): void;
}

interface TargetBounds
{
  min_y: number;
  height_range: number;
  center_x: number;
  max_dx: number;
  z_spread: number;
}

interface FieldBounds
{
  per_table: Map<number, TargetBounds>;
  deepest_table_z: number;
}

export class GameBot
{
  private static readonly SHOT_DELAY_MIN = 0.1;
  private static readonly SHOT_DELAY_MAX = 0.3;

  private static readonly AIM_SPREAD = 0.28;
  private static readonly AIM_CENTER_BIAS = 0.1;
  private static readonly REPEAT_CHANCE = 0.15;
  private static readonly REPEAT_RADIUS = 1.4;
  private static readonly SCORE_NOISE = 0.1;
  private static readonly GREED_POWER = 8;

  private static readonly W_CENTER_HEIGHT = 2.6;
  private static readonly W_CENTER_X = 1.1;
  private static readonly W_SUPPORTS = 0.55;
  private static readonly W_SUPPORTS_CAP = 3.0;
  private static readonly W_JAR = 1.8;
  private static readonly W_JAR_BURIED = 1.4;
  private static readonly W_CONE = -1.2;

  private static readonly MIN_SCORE = 0.05;
  private static readonly PEAK = 0.25;

  private static readonly MULTI_ROW_Z = 1.0;
  private static readonly BURST_CHANCE = 0;

  private static readonly REACH_ATTEMPTS = 4;

  private static aim_picks = 0;
  private static blocked_picks = 0;

  public static aimStats(): { picks: number; blocked: number; share: number }
  {
    return {
      picks: GameBot.aim_picks,
      blocked: GameBot.blocked_picks,
      share: GameBot.aim_picks > 0 ? GameBot.blocked_picks / GameBot.aim_picks : 0,
    };
  }

  public static resetAimStats(): void
  {
    GameBot.aim_picks = 0;
    GameBot.blocked_picks = 0;
    GameBot.shot_log.length = 0;
  }

  public static shot_log: { table: number; alive: number }[] = [];

  private timer = 0;
  private shots_done = 0;
  private last_aim: THREE.Vector3 | null = null;
  private burst_pending = false;

  constructor( private readonly game: BotGameApi ) {}

  public getShotsDone(): number
  {
    return this.shots_done;
  }

  public update( dt: number ): void
  {
    this.timer -= dt;

    if ( !this.canShoot() )
      return;

    const targets = this.game.getAliveTargets();

    if ( targets.length === 0 )
      return;

    const bounds = this.measurePerTable( targets );
    const aim = this.chooseAimPoint( targets, bounds );

    if ( aim === null )
    {
      // Чекаємо наступного physics tick.
      this.timer = 0.05;
      return;
    }

    this.game.botFireAt( aim );

    this.last_aim = aim.clone();
    this.shots_done++;

    this.timer = this.nextDelay( bounds );
  }

  private canShoot(): boolean
  {
    return this.timer <= 0
      && !this.game.isFinished()
      && this.game.getShotsLeft() > 0
      && !this.game.hasActiveBalls()
      && this.game.areTargetsSettled();
  }

  private nextDelay( bounds: FieldBounds ): number
  {
    if ( this.burst_pending )
    {
      this.burst_pending = false;
      return 0;
    }

    const multi_row = bounds.deepest_table_z >= GameBot.MULTI_ROW_Z;

    if ( multi_row && Math.random() < GameBot.BURST_CHANCE )
    {
      this.burst_pending = true;
      return 0;
    }

    return THREE.MathUtils.randFloat(
      GameBot.SHOT_DELAY_MIN,
      GameBot.SHOT_DELAY_MAX
    );
  }

  private chooseAimPoint(
    targets: BotTarget[],
    bounds: FieldBounds
  ): THREE.Vector3 | null
  {
    const tables = new Map<number, BotTarget[]>();

    for ( const target of targets )
    {
      const group = tables.get( target.table );

      if ( group )
        group.push( target );
      else
        tables.set( target.table, [target] );
    }

    // Спочатку вибираємо найбільш перспективний стіл.
    const rankedTables = [...tables.entries()]
      .map( ( [table, tableTargets] ) =>
      {
        const reachable = tableTargets.filter( target =>
        {
          const aim = target.position.clone();
          aim.y -= target.size.y * GameBot.AIM_CENTER_BIAS;

          return this.game.isReachable( aim );
        } );

        const score = tableTargets.reduce(
          ( sum, target ) =>
            sum + this.scoreTarget( target, tableTargets, bounds ),
          0
        );

        return {
          table,
          targets: tableTargets,
          reachable,
          score,
        };
      } )
      .filter( x => x.reachable.length > 0 )
      .sort( ( a, b ) =>
      {
        // Спочатку столи, де є досяжні цілі.
        if ( a.reachable.length !== b.reachable.length )
          return b.reachable.length - a.reachable.length;

        return b.score - a.score;
      } );

    for ( const table of rankedTables )
    {
      const target = this.pickTarget(
        table.reachable,
        bounds
      );

      const aim = target.position.clone();

      aim.y -= target.size.y * GameBot.AIM_CENTER_BIAS;

      const shot = this.applySpread(
        aim,
        GameBot.AIM_SPREAD
      );

      GameBot.aim_picks++;

      if ( this.game.isReachable( shot ) )
        return shot;

      GameBot.blocked_picks++;
    }

    return null;
  }

  private shouldRepeatLastShot( targets: BotTarget[] ): boolean
  {
    if ( this.last_aim === null )
      return false;

    if ( Math.random() >= GameBot.REPEAT_CHANCE )
      return false;

    return targets.some(
      t => t.position.distanceTo( this.last_aim! ) < GameBot.REPEAT_RADIUS
    );
  }

  private pickTarget( targets: BotTarget[], bounds: FieldBounds ): BotTarget
  {
    let total = 0;

    const weights = targets.map( t =>
    {
      const noise = 1 + THREE.MathUtils.randFloatSpread( GameBot.SCORE_NOISE );
      const weight = Math.pow( this.scoreTarget( t, targets, bounds ) * noise, GameBot.GREED_POWER );
      total += weight;
      return weight;
    } );

    let roll = Math.random() * total;

    for ( let i = 0; i < targets.length; i++ )
    {
      roll -= weights[i];

      if ( roll <= 0 )
        return targets[i];
    }

    return targets[targets.length - 1];
  }

  private measurePerTable( targets: BotTarget[] ): FieldBounds
  {
    const grouped = new Map<number, BotTarget[]>();

    for ( const t of targets )
    {
      const group = grouped.get( t.table );

      if ( group )
        group.push( t );
      else
        grouped.set( t.table, [t] );
    }

    const per_table = new Map<number, TargetBounds>();
    let deepest_table_z = 0;

    for ( const [table, group] of grouped )
    {
      const bounds = this.measure( group );
      per_table.set( table, bounds );
      deepest_table_z = Math.max( deepest_table_z, bounds.z_spread );
    }

    return { per_table, deepest_table_z };
  }

  private measure( targets: BotTarget[] ): TargetBounds
  {
    let min_y = Infinity;
    let max_y = -Infinity;
    let min_z = Infinity;
    let max_z = -Infinity;
    let sum_x = 0;

    for ( const t of targets )
    {
      min_y = Math.min( min_y, t.position.y );
      max_y = Math.max( max_y, t.position.y );
      min_z = Math.min( min_z, t.position.z );
      max_z = Math.max( max_z, t.position.z );
      sum_x += t.position.x;
    }

    const center_x = sum_x / targets.length;

    let max_dx = 0.001;

    for ( const t of targets )
      max_dx = Math.max( max_dx, Math.abs( t.position.x - center_x ) );

    return {
      min_y,
      height_range: Math.max( 0.001, max_y - min_y ),
      center_x,
      max_dx,
      z_spread: max_z - min_z,
    };
  }

  private scoreTarget(
    target: BotTarget,
    all: BotTarget[],
    bounds: FieldBounds
  ): number
  {
    const table = bounds.per_table.get( target.table );

    if ( !table )
      return GameBot.MIN_SCORE;

    const height_ratio = ( target.position.y - table.min_y ) / table.height_range;
    const height_score = height_ratio < GameBot.PEAK
      ? height_ratio / GameBot.PEAK
      : 1 - ( height_ratio - GameBot.PEAK ) / ( 1 - GameBot.PEAK );

    const centrality = 1 - Math.abs( target.position.x - table.center_x ) / table.max_dx;
    const supports = this.countSupported( target, all );

    let score = 1;

    score += height_score * GameBot.W_CENTER_HEIGHT;
    score += centrality * GameBot.W_CENTER_X;
    score += Math.min( supports * GameBot.W_SUPPORTS, GameBot.W_SUPPORTS_CAP );

    if ( target.kind === "jar" )
    {
      score += GameBot.W_JAR;
      score += GameBot.W_JAR_BURIED * Math.min( 1, supports / 2 );
    }

    if ( target.kind === "cone" )
      score += GameBot.W_CONE;

    return Math.max( GameBot.MIN_SCORE, score );
  }

  private countSupported( target: BotTarget, all: BotTarget[] ): number
  {
    const radius_x = target.size.x * 0.6;
    const radius_z = target.size.z * 0.6;

    let count = 0;

    for ( const other of all )
    {
      if ( other === target )
        continue;

      if ( other.table !== target.table )
        continue;

      if ( other.position.y <= target.position.y )
        continue;

      if ( Math.abs( other.position.x - target.position.x ) > radius_x )
        continue;

      if ( Math.abs( other.position.z - target.position.z ) > radius_z )
        continue;

      count++;
    }

    return count;
  }

  private applySpread( point: THREE.Vector3, spread: number ): THREE.Vector3
  {
    point.x += THREE.MathUtils.randFloatSpread( spread );
    point.y += THREE.MathUtils.randFloatSpread( spread );

    return point;
  }
}